import React, { useEffect, useState } from 'react';
import { submitFormData } from '../../../../pages/api';

const Lamination = ({ formData = {}, onFormDataChange }) => {
    // Destructure formData to get initial values
    const {
        laminationObservations = [],
        peelOffObservations = [],
        nonConformance = '',
    } = formData;

    // State to hold table data (product, specifications, frequency)
    const [laminationData, setLaminationData] = useState([]);
    const [peelOffData, setPeelOffData] = useState([]);

    // Fetch metadata and inspectionData only once on component mount
    useEffect(() => {
        const metadata = JSON.parse(sessionStorage.getItem('metadata'));
        const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

        // Initialize table data from metadata
        if (metadata && metadata.post && metadata.post.Lamination_post) {
            const { LaminationParametersVI, PeelOffTest } = metadata.post.Lamination_post;
            setLaminationData(LaminationParametersVI || []);
            setPeelOffData(PeelOffTest || []);
        }

        // Initialize observations and non-conformance from inspectionData only if formData is empty
        if (
            inspectionData &&
            inspectionData.PostLamination &&
            inspectionData.PostLamination.Lamination_post &&
            (!laminationObservations.length && !peelOffObservations.length && !nonConformance.trim())
        ) {
            const { LaminationParametersVI, PeelOffTest, nonConformance: inspectionNonConformance } =
                inspectionData.PostLamination.Lamination_post;

            // Map observation data from inspectionData
            const prefilledLaminationObservations = LaminationParametersVI?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            const prefilledPeelOffObservations = PeelOffTest?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            // Update formData with observations and non-conformance
            onFormDataChange({
                ...formData,
                laminationObservations: prefilledLaminationObservations,
                peelOffObservations: prefilledPeelOffObservations,
                nonConformance: inspectionNonConformance || '',
            });
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleObservationChange = (index, field, value, isLamination) => {
        const fieldKey = isLamination ? 'laminationObservations' : 'peelOffObservations';
        const updatedObservations = [...(formData[fieldKey] || [])];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, [fieldKey]: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        // Combine table data with observations
        const combinedLaminationData = laminationData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.laminationObservations?.[index]?.observation1 || '',
            observation2: formData.laminationObservations?.[index]?.observation2 || '',
        }));

        const combinedPeelOffData = peelOffData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.peelOffObservations?.[index]?.observation1 || '',
            observation2: formData.peelOffObservations?.[index]?.observation2 || '',
        }));

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PostLamination: {
                Lamination_post: {
                    LaminationParametersVI: combinedLaminationData,
                    PeelOffTest: combinedPeelOffData,
                    nonConformance: formData.nonConformance || '',
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Observations submitted successfully');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Lamination Inspection</h3>

            {/* Lamination Parameters & Visual Inspection */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Lamination Parameters & Visual Inspection</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {laminationData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}
                            >
                                <td className="py-3 px-4">{item.product || ''}</td>
                                <td className="py-3 px-4">{item.specifications || ''}</td>
                                <td className="py-3 px-4">{item.frequency || ''}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.laminationObservations?.[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, true)}
                                        placeholder="Enter observation 1"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.laminationObservations?.[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, true)}
                                        placeholder="Enter observation 2"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Peel-off Test */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Peel-off Test</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {peelOffData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}
                            >
                                <td className="py-3 px-4">{item.product || ''}</td>
                                <td className="py-3 px-4">{item.specifications || ''}</td>
                                <td className="py-3 px-4">{item.frequency || ''}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.peelOffObservations?.[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, false)}
                                        placeholder="Enter observation 1"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.peelOffObservations?.[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, false)}
                                        placeholder="Enter observation 2"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Non Conformance */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={formData.nonConformance || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border p-4 rounded-lg focus:outline-none"
                    rows={4}
                />
            </div>

            {/* Submit Observations */}
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500"
            >
                Submit
            </button>
        </div>
    );
};

export default Lamination;